import { formatNumber } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';
import { SimpleApi } from '@core/api/simple.api';
import { Kunde } from '@features/kunder/kunde.model';
import dayjs from 'dayjs';
import { LetsikringSearchParams } from './letregister-search/letsikring-search-params.model';
import { LetsikringOpsparetVaerdi } from './letsikring-opsparet-vaerdi.model';
import { LetsikringRevision } from './letsikring-revision.model';
import { LetsikringRisiko } from './letsikring-risiko.model';
import { LetsikringDatasource } from './letsikring.datasource';
import { Letsikring } from './letsikring.model';

@Injectable({
  providedIn: 'root',
})
export class LetregisterService {
  simplApi = inject(SimpleApi);
  letsikringDatasource = inject(LetsikringDatasource);
  simpleApi = inject(SimpleApi);

  selectedLetsikring = signal<Letsikring | undefined>(undefined);
  selectedKunde = signal<Kunde | undefined>(undefined);
  activeTab = signal('search');
  refreshDate = signal<Date>(new Date());

  searchLetsikring(searchParams: LetsikringSearchParams) {
    this.selectedKunde.set(undefined);
    this.selectedLetsikring.set(undefined);
    this.letsikringDatasource.searchParams.set(searchParams);
    return this.letsikringDatasource.loadData();
  }

  async selectLetsikringResultRow(letsikring: Letsikring) {
    this.selectLetsikring(letsikring);

    if (letsikring) {
      this.activeTab.set('detail');
    }
  }

  async selectLetsikring(letsikring: Letsikring) {
    const l = await this.letsikringDatasource.getById(letsikring.id!);
    this.selectedLetsikring.set(l);

    this.activeTab.set('detail');
    this.refreshDate.set(new Date());
  }

  async opsparetVaerdi(letsikring: Letsikring) {
    return await this.simplApi.getSingle<LetsikringOpsparetVaerdi>(
      '/api/admin/letsikringer/opsparetvaerdi',
      { letsikringId: letsikring.id! }
    );
  }

  async gemLetsikring(letsikring: Partial<Letsikring>) {
    const existingLetsikring = this.selectedLetsikring()!;
    if (!existingLetsikring.gaeldendeRevision.letsikringRisiko) {
      delete letsikring.gaeldendeRevision?.letsikringRisiko;
    }
    if (!existingLetsikring.gaeldendeRevision.letsikringAfIndtaegt) {
      delete letsikring.gaeldendeRevision?.letsikringAfIndtaegt;
    }
    if (!existingLetsikring.gaeldendeRevision.letsikringTilSelvstaendige) {
      delete letsikring.gaeldendeRevision?.letsikringTilSelvstaendige;
    }

    await this.reSelectLetsikring();
  }

  selectKunde(kunde: Kunde) {
    this.selectedKunde.set(kunde);
    this.activeTab.set('aftaler');
  }

  async reSelectLetsikring() {
    await this.selectLetsikring(this.selectedLetsikring()!);
  }

  getProduktNavn(letsikring: Letsikring) {
    let navn = '';
    if (letsikring.gaeldendeRevision.letsikringAfIndtaegt) {
      navn = 'Letsikring af indtægt';
    }
    if (letsikring.gaeldendeRevision.letsikringRisiko) {
      navn = letsikring.gaeldendeRevision.letsikringRisiko.produktNavn || '';
    }
    if (letsikring.gaeldendeRevision.letsikringTilSelvstaendige) {
      navn += ' (LTS)';
    }
    return navn;
  }

  isFlydendeDaekning(letsikringRisiko: LetsikringRisiko) {
    return letsikringRisiko.letsikringRisikoProduktvalgId != null;
  }

  getGruppeProduktAarsData(letsikringRisiko?: LetsikringRisiko) {
    if (letsikringRisiko?.gruppeProdukt?.gruppeProduktAarsData) {
      const aar = dayjs().year();
      const aarsData =
        letsikringRisiko?.gruppeProdukt?.gruppeProduktAarsData?.filter(
          d => d.aarstal == aar
        );
      if (aarsData?.length > 0) {
        return aarsData[0];
      }
    }
    return undefined;
  }

  getProduktType(revision: LetsikringRevision) {
    return revision.letsikringRisiko?.gruppeProdukt?.produktType?.type || '';
  }

  getTilbudsnr(revision: LetsikringRevision) {
    const aftaleDokument = revision.aftaleDokument;
    if (aftaleDokument) {
      if (aftaleDokument.dokumentType?.navn === 'SK501') {
        //PI skifte
        return aftaleDokument.beskrivelse!;
      } else {
        const tilbudNr = revision.tilbudNr;
        if (tilbudNr != null) {
          const letsikring = this.selectedLetsikring();
          return (
            letsikring?.kunde.cprNummer +
            '-' +
            formatNumber(tilbudNr, 'da', '3.0')
          );
        }
      }
    }
    return undefined;
  }
}
